"use client";
import Link from "next/link";
import { motion } from "framer-motion";
import Tilt3D from "./Tilt3D";
import PathIllustration, { type PathIllustrationKind } from "./PathIllustration";

interface PathCardProps {
  index: number;
  href: string;
  external?: boolean;
  title: string;
  illustration: PathIllustrationKind;
  accent: "gold" | "fuchsia" | "cyan" | "violet" | "orange";
  size?: "sm" | "md" | "lg";
  /**
   * Suppress the framer-motion entrance reveal entirely.
   *
   * Used by `PathsHorizontalReveal` for the mobile carousel. Inside a
   * horizontally-scrolling snap container, only 1-2 cards are ever in
   * the window viewport at once; cards waiting in the queue are
   * positioned at large positive x offsets and never trigger the
   * `whileInView` IntersectionObserver, which would otherwise leave
   * them permanently stuck at their initial transform (the original
   * "cards cut off from the top" bug). The carousel instead asks
   * PathCard to render in its final visual state from the start, so
   * snapping to any card always shows the card correctly with no
   * dependency on viewport observation.
   */
  noReveal?: boolean;
  /** Forward to PathIllustration — false freezes animations on hidden slides. */
  animated?: boolean;
}

const ACCENT_GLOW: Record<PathCardProps["accent"], string> = {
  gold:    "hover:shadow-[0_50px_120px_-30px_rgba(245,185,69,0.85)]",
  fuchsia: "hover:shadow-[0_50px_120px_-30px_rgba(219,39,119,0.85)]",
  cyan:    "hover:shadow-[0_50px_120px_-30px_rgba(34,211,238,0.85)]",
  violet:  "hover:shadow-[0_50px_120px_-30px_rgba(139,92,246,0.85)]",
  orange:  "hover:shadow-[0_50px_120px_-30px_rgba(249,115,22,0.85)]",
};

/** Continuous pulsating accent-color glow per card so each one breathes
 *  in its own colour even when the user isn't hovering. */
const ACCENT_PULSE: Record<PathCardProps["accent"], string> = {
  gold:    "pulse-glow-gold",
  fuchsia: "pulse-glow-fuchsia",
  cyan:    "pulse-glow-cyan",
  violet:  "pulse-glow-violet",
  orange:  "pulse-glow-orange",
};

/** Inset pulsating glow — stays INSIDE the card bounds (inset box-shadow
 *  is clipped by border-radius + overflow-hidden). Cannot bleed onto
 *  adjacent prism faces during swipe rotation. */
const INNER_GLOW: Record<PathCardProps["accent"], string> = {
  gold:    "pi-glow-gold",
  fuchsia: "pi-glow-fuchsia",
  cyan:    "pi-glow-cyan",
  violet:  "pi-glow-violet",
  orange:  "pi-glow-orange",
};

/** Outer halo glow — box-shadow outside the card, pulses accent colour.
 *  Uses pc-outer-glow-* (no 1px ring) so safe on OLED phones. */
const OUTER_GLOW: Record<PathCardProps["accent"], string> = {
  gold:    "pc-outer-glow-gold",
  fuchsia: "pc-outer-glow-fuchsia",
  cyan:    "pc-outer-glow-cyan",
  violet:  "pc-outer-glow-violet",
  orange:  "pc-outer-glow-orange",
};

/** Mobile card outer halo — box-shadow kept within the 24px face padding so
 *  it never hits the section overflow:hidden boundary. Opacity-only animation
 *  (pcGlowFade) keeps it GPU-composited with zero repaint cost. */
const MOBILE_SHADOW: Record<PathCardProps["accent"], string> = {
  gold:    "0 0 20px 2px rgba(245,185,69,0.60)",
  fuchsia: "0 0 20px 2px rgba(236,72,153,0.60)",
  cyan:    "0 0 20px 2px rgba(34,211,238,0.60)",
  violet:  "0 0 20px 2px rgba(167,139,250,0.60)",
  orange:  "0 0 20px 2px rgba(249,115,22,0.60)",
};

const ACCENT_RING: Record<PathCardProps["accent"], string> = {
  gold:    "from-amber-300/70 via-amber-400/15 to-transparent",
  fuchsia: "from-fuchsia-300/70 via-fuchsia-500/15 to-transparent",
  cyan:    "from-cyan-300/70 via-cyan-500/15 to-transparent",
  violet:  "from-violet-300/70 via-violet-500/15 to-transparent",
  orange:  "from-orange-300/70 via-orange-500/15 to-transparent",
};

const ACCENT_CHIP: Record<PathCardProps["accent"], string> = {
  gold:    "text-amber-200 bg-amber-300/15 ring-amber-300/30",
  fuchsia: "text-fuchsia-200 bg-fuchsia-400/15 ring-fuchsia-300/30",
  cyan:    "text-cyan-200 bg-cyan-300/15 ring-cyan-300/30",
  violet:  "text-violet-200 bg-violet-300/15 ring-violet-300/30",
  orange:  "text-orange-200 bg-orange-300/15 ring-orange-300/30",
};

const BG_TINT: Record<PathCardProps["accent"], string> = {
  gold:    "from-amber-500/30 via-amber-700/15 to-ink-950/55",
  fuchsia: "from-fuchsia-500/32 via-fuchsia-700/15 to-ink-950/55",
  cyan:    "from-cyan-500/30 via-cyan-700/12 to-ink-950/55",
  violet:  "from-violet-500/28 via-violet-700/12 to-ink-950/55",
  orange:  "from-orange-500/32 via-orange-700/15 to-ink-950/55",
};

/** Per-accent opacity multiplier applied inline on the BG_TINT div.
 *  Cyan and violet are perceptually far brighter than gold/orange on
 *  dark backgrounds — these inline overrides are build-cache safe
 *  (inline style always beats Tailwind opacity modifiers). */
const BG_OPACITY: Record<PathCardProps["accent"], number> = {
  gold:    1,
  fuchsia: 1,
  cyan:    0.40,
  violet:  0.38,
  orange:  1,
};

export default function PathCard({
  index,
  href,
  external,
  title,
  illustration,
  accent,
  size = "md",
  noReveal = false,
  animated = true,
}: PathCardProps) {
  const Tag: any = external ? "a" : Link;
  const linkProps = external
    ? { href, target: "_blank", rel: "noopener noreferrer" }
    : { href };

  const floatDelay = `${(index * 0.55).toFixed(2)}s`;
  const floatDuration = `${7 + (index % 3) * 0.6}s`;
  const aspect = size === "lg" ? "aspect-[4/5]" : size === "sm" ? "aspect-[3/4]" : "aspect-[3/4]";
  const radius = size === "sm" ? "rounded-[1.25rem]" : "rounded-[2rem]";
  const titleClass =
    size === "sm"
      ? "text-base md:text-lg lg:text-xl xl:text-2xl"
      : "text-xl sm:text-2xl";
  const labelClass = size === "sm" ? "mt-2 text-[9px] tracking-[0.22em]" : "mt-3 text-[10px] tracking-[0.3em]";
  const chipClass = size === "sm" ? "px-2 py-0.5 text-[9px] tracking-[0.18em]" : "px-3 py-1 text-[11px] tracking-[0.25em]";
  const textPad = size === "sm" ? "p-3 md:p-4 xl:p-5" : "p-6";
  // `noReveal` and `size === "sm"` BOTH disable the entrance reveal.
  // The mobile carousel passes `noReveal` to keep the default `md`
  // visual styling (fonts, padding, radius, aspect) while still
  // dropping the viewport-triggered animation that would otherwise
  // strand off-screen-x cards at their initial transform.
  //
  // v6.10 (2026-05): the previous loading-screen entrance gate was
  // REMOVED here. The home page paths section sits BELOW the
  // CosmicJourney hero (the welcome scene takes the full first
  // viewport), so the cards are never above-the-fold on first paint
  // — `whileInView` reliably fires when the user scrolls down to
  // them and the splash is long gone by then. Adding the gate on
  // top of that combo was stranding the cards at their `initial`
  // transform (opacity 0, y 80, scale 0.85) when the loading flag
  // hadn't propagated yet, which is what the user reported as "path
  // cards not scrollable, illustrations gone".
  const revealProps =
    noReveal || size === "sm"
      ? {
          initial: false as const,
          animate: undefined,
          transition: undefined,
        }
      : {
          initial: { opacity: 0, y: 80, scale: 0.85, rotateX: 18 },
          whileInView: { opacity: 1, y: 0, scale: 1, rotateX: 0 },
          viewport: { once: true, amount: 0.05 },
          transition: {
            duration: 1.0,
            delay: index * 0.12,
            ease: [0.22, 1, 0.36, 1],
          },
        };

  // floatSlow is disabled in two cases:
  //   • size === "sm" — small dense grids look busy when floating.
  //   • noReveal — used by the mobile horizontal scroll-snap
  //     carousel. The carousel track has `overflow-y: hidden` so
  //     a 12 px upward float would clip the top edge of the card
  //     ("path cards still cut off during floating"). On the
  //     mobile carousel we keep the cards perfectly still — the
  //     swipe motion itself supplies all the kinaesthetic
  //     feedback, and removing the float also frees the GPU from
  //     animating ten extra translateY keyframes per second.
  const floatDisabled = size === "sm" || noReveal;

  /* ── Mobile carousel path: render a flat, layer-light card ──
   * The mobile horizontal carousel passes `noReveal: true`. On
   * iOS we then skip:
   *   • framer-motion `motion.div` wrapper (it forces a separate
   *     compositor layer for transform animation),
   *   • Tilt3D (4 springs + nested motion.div + preserve-3d
   *     wrapper per card — wasted on a touch-only device that
   *     can't tilt with the cursor),
   *   • the outer perspective:1200px / preserve-3d 3D context,
   *   • the floatSlow keyframe (already gated by floatDisabled).
   * What's left is a plain anchor with the visual styling intact
   * (gradient ring, illustration, chip, title). One DOM tree, no
   * extra compositor layers, no per-frame work — this is the
   * cheapest the card can possibly be while still looking the
   * same. Combined with the carousel's flat (no scale/opacity)
   * rendering, this removes all the GPU pressure that was
   * causing the "path cards still laggy and cut off both
   * directions swipe" report. */
  if (noReveal) {
    return (
      <div
        data-testid={`path-card-${index + 1}`}
        className="group relative h-full"
        data-cursor="hover"
        data-cursor-label={title}
        // v6.13.7 — Float restored as a *non-translating* breathing
        // pulse. Earlier attempts (floatSlow 14 px, floatSlowMobile
        // 5 px) both used translateY inside a prism face whose
        // overflow:hidden clips any vertical movement at the chip
        // and bottom-title edges. floatBreathe replaces the
        // translation with scale 1 → 1.035 + rotateZ 0.35° so the
        // card visibly "breathes" but its centre never moves —
        // physically impossible to clip the face. Stagger via
        // floatDelay keeps neighbouring cards out of phase.
        style={{
          animation: `floatBreathe ${floatDuration} ease-in-out ${floatDelay} infinite`,
          transformOrigin: "center center",
          willChange: "transform",
        }}
      >
        {/* Mobile outer halo: box-shadow (22 px reach) fits inside the 24 px face padding — never hits section overflow:hidden.
             pcGlowFade animates opacity only → GPU composited, zero repaint. */}
        <div
          aria-hidden="true"
          className={`absolute ${radius} pointer-events-none`}
          style={{ inset: 0, boxShadow: MOBILE_SHADOW[accent], animation: "pcGlowFade 5.0s ease-in-out infinite" }}
        />
        <Tag
          {...linkProps}
          data-testid={`path-card-${index + 1}-link`}
          className={`relative block h-full overflow-hidden ${radius} glass-strong`}
          style={{
            // FULLY OPAQUE — solid gradient prevents prism bleed-through.
            background: "linear-gradient(180deg, rgb(18,16,30), rgb(8,8,16))",
            // Override glass-strong's white border
            border: "none",
            // Remove backdrop-filter compositing edge — glass-strong applies
            // backdrop-filter but the opaque gradient covers it completely, so
            // it only produces a faint ghost edge at the card boundary.
            backdropFilter: "none",
            WebkitBackdropFilter: "none",
          }}
          // v6.13.17 — Restored accent-coloured pulse glow on the
          // mobile carousel branch too. Body stays solid (no
          // bleed-through of swiped cards behind, that fix from
          // v6.13.13 is preserved by the opaque gradient + inner
          // boxShadow above) but the OUTER halo is now in the
          // card's accent colour, matching the desktop look the
          // user just asked to bring back.
          data-mobile-glow
        >
          <div
            aria-hidden="true"
            className={`pointer-events-none absolute inset-0 ${radius} bg-gradient-to-br ${ACCENT_RING[accent]} opacity-60`}
            style={{
              padding: "1px",
              WebkitMask:
                "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
              WebkitMaskComposite: "xor",
              maskComposite: "exclude",
            }}
          />
          {/*
            Mobile carousel inner content fills the ENTIRE Swiper cube
            face. Previously this used `aspect-[3/4]` (=0.75 ratio)
            inside a Swiper slide that the mobile cube sizes at
            ~1/1.42 (=0.70 ratio) — so the inner block was ~88 % of
            the slide height and sat anchored to the top, leaving
            ~30 px of empty glass-strong gradient at the bottom of
            every card. The user reported that as "path cards still
            missing illustrations": the illustration WAS rendering,
            it just wasn't filling the visible card face on mobile,
            so the bottom third of each card looked blank. Switching
            to `h-full w-full` makes the illustration + tint + chip +
            title fill the whole card face every time.
          */}
          <div className={`relative h-full w-full overflow-hidden ${radius}`}>
            <div className={`absolute inset-0 bg-gradient-to-br ${BG_TINT[accent]}`} style={{ opacity: BG_OPACITY[accent] }} />
            <PathIllustration
              kind={illustration}
              accent={accent}
              animated={animated}
              noFilter
            />
            <div
              aria-hidden="true"
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, transparent 30%, rgba(5,6,10,0.55) 70%, rgba(5,6,10,0.92) 100%)",
              }}
            />
            <div aria-hidden="true" className={`pointer-events-none absolute inset-0 ${INNER_GLOW[accent]}`} style={{ mixBlendMode: "lighten" }} />
            <div className="absolute left-5 top-5">
              <span
                data-testid={`path-card-${index + 1}-number`}
                className={`heading-display rounded-full ${chipClass} font-semibold uppercase ring-1 ${ACCENT_CHIP[accent]}`}
                // Mobile carousel only: the desktop chip relies on backdrop-blur-md
                // to frost the bright illustration behind it so the light accent
                // digits stay legible. That blur was dropped on mobile for perf, so
                // the palest accents (cyan = card 02) washed out over the bright card
                // top and the number "vanished". A dark text-shadow halo restores
                // contrast on every accent without re-adding a compositor blur layer.
                style={{ textShadow: "0 1px 6px rgba(0,0,0,0.95), 0 1px 2px rgba(0,0,0,0.9)" }}
              >
                {String(index + 1).padStart(2, "0")}
              </span>
            </div>
            <div className={`absolute inset-x-0 bottom-0 ${textPad}`}>
              <h3
                data-testid={`path-card-${index + 1}-title`}
                className={`heading-display text-balance ${titleClass} font-bold uppercase leading-tight text-white drop-shadow-[0_2px_18px_rgba(0,0,0,0.85)]`}
              >
                {title}
              </h3>
              <div
                data-testid={`path-card-${index + 1}-enter-label`}
                className={`${labelClass} inline-flex items-center justify-center gap-1.5 font-semibold uppercase text-white/70`}
              >
                Enter
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M5 12h14" />
                  <path d="m12 5 7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>
        </Tag>
      </div>
    );
  }

  return (
    <motion.div
      data-testid={`path-card-${index + 1}`}
      {...revealProps}
      suppressHydrationWarning
      className="group relative h-full"
      style={{ transformStyle: "preserve-3d", perspective: "1200px" }}
      data-cursor="hover"
      data-cursor-label={title}
    >
      {/*
       * floatSlow restored for `md` cards (the default and the
       * mobile carousel size). Only disabled for size === "sm" to
       * keep dense small-card grids from feeling busy. The mobile
       * sticky-pin carousel does NOT clip vertically — cards live
       * inside a position:sticky / overflow:hidden 100vh container
       * with the cards vertically centered and ample headroom — so
       * the floatSlow ~12 px lift breathes correctly without ever
       * touching an edge.
       */}
      <div style={{ animation: floatDisabled ? "none" : `floatSlow ${floatDuration} ease-in-out ${floatDelay} infinite` }} className="h-full">
        <Tilt3D intensity={0.85} className="h-full">
          {/* Outer glow — inset:0 flush on card; filter:blur(48px) fans colour 48px outward, no hard ring */}
          {/* perf: translateZ(0)+backface-visibility promote this blurred layer to its own
              composited texture so the 48px blur is rasterised ONCE; the entrance
              scale/rotateX on the ancestor then samples that texture instead of
              re-running the blur filter every frame (×5 cards during Lenis autoscroll).
              Identity transform on a non-text, no-blend solid-colour layer → pixel-identical. */}
          <div aria-hidden="true" className={`absolute ${radius} pointer-events-none ${OUTER_GLOW[accent]}`} style={{ inset: 0, transform: "translateZ(0)", backfaceVisibility: "hidden" }} />
          <Tag
            {...linkProps}
            data-testid={`path-card-${index + 1}-link`}
            className={`relative block h-full overflow-hidden ${radius} glass-strong transition-all duration-500 ${ACCENT_GLOW[accent]}`}
            style={{
              background: "linear-gradient(180deg, rgb(18,16,30), rgb(8,8,16))",
              // Override glass-strong's 1px white border ring
              border: "none",
              // AUTOSCROLL PERF FIX — drop glass-strong's live
              // backdrop-filter: blur(28px). The opaque gradient above already
              // covers the card completely (identical to the mobile/noReveal
              // branch above), so the blur is VISUALLY INERT — yet it forced a
              // full 28px backdrop re-rasterisation over the moving page every
              // frame, for all ~5 cards, during the Lenis hero→#paths
              // autoscroll. That was the dominant cause of the glide "freezing
              // up". The existing `#paths .liquid-glass-3d` CSS override never
              // matched these cards (they carry `glass-strong`, not
              // `liquid-glass-3d`), so the blur was live on desktop.
              backdropFilter: "none",
              WebkitBackdropFilter: "none",
            }}
          >
            {/* Animated gradient ring */}
            <div
              aria-hidden="true"
              className={`pointer-events-none absolute inset-0 ${radius} bg-gradient-to-br ${ACCENT_RING[accent]} opacity-60 transition-opacity duration-500 group-hover:opacity-100`}
              style={{ padding: "1px", WebkitMask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)", WebkitMaskComposite: "xor", maskComposite: "exclude" }}
            />

            <div
              className={`relative ${aspect} overflow-hidden ${radius}`}
              style={{ transform: "translateZ(28px)", transformStyle: "preserve-3d" }}
            >
              {/* Vector illustration backdrop — renders inline SVG with
                  scene-specific shapes, gradients and floating accents. No
                  raster image (so no visible photo borders / pixel edges). */}
              <div className={`absolute inset-0 bg-gradient-to-br ${BG_TINT[accent]}`} style={{ opacity: BG_OPACITY[accent] }} />
              <PathIllustration kind={illustration} accent={accent} animated={animated} />
              <div
                aria-hidden="true"
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, transparent 30%, rgba(5,6,10,0.55) 70%, rgba(5,6,10,0.92) 100%)",
                }}
              />
              <div aria-hidden="true" className={`pointer-events-none absolute inset-0 ${INNER_GLOW[accent]}`} style={{ mixBlendMode: "lighten" }} />
              {/* Inner shimmer on hover — toned WAY down (was via-white/25
                  which produced an obvious white strip across the card
                  even at low scroll velocities). 8% white + a slower
                  sweep keeps the polish without the laundry-bar look. */}
              <div
                aria-hidden="true"
                className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/[0.08] to-transparent opacity-0 transition-all duration-[1400ms] group-hover:translate-x-full group-hover:opacity-100"
              />
              {/* Index chip */}
              <div
                className="absolute left-5 top-5"
                style={{ transform: "translateZ(40px)" }}
              >
                <span data-testid={`path-card-${index + 1}-number`} className={`heading-display rounded-full ${chipClass} font-semibold uppercase ring-1 backdrop-blur-md ${ACCENT_CHIP[accent]}`}>
                  {String(index + 1).padStart(2, "0")}
                </span>
              </div>
              {/* Label */}
              <div
                className={`absolute inset-x-0 bottom-0 ${textPad}`}
                style={{ transform: "translateZ(56px)" }}
              >
                <h3 data-testid={`path-card-${index + 1}-title`} className={`heading-display text-balance ${titleClass} font-bold uppercase leading-tight text-white drop-shadow-[0_2px_18px_rgba(0,0,0,0.85)]`}>
                  {title}
                </h3>
                <div data-testid={`path-card-${index + 1}-enter-label`} className={`${labelClass} inline-flex items-center justify-center gap-1.5 font-semibold uppercase text-white/70 transition-all duration-300 group-hover:gap-3 group-hover:text-white`}>
                  Enter
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <path d="M5 12h14" />
                    <path d="m12 5 7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
          </Tag>
        </Tilt3D>
      </div>
    </motion.div>
  );
}
